/**
 * BB-14d2c2 -- microphone acquisition, in exactly one place.
 *
 * **Why this is its own module.** `getUserMedia` is the single call in
 * the browser media path that can prompt, hang, or be permanently
 * denied by policy, and its failure vocabulary is a browser detail
 * (`NotAllowedError`, `NotFoundError`, ...) that must never reach an
 * agent. Isolating it means the rest of the media path deals only with
 * a bounded reason, and the structural guards can assert that no other
 * module asks for a microphone.
 *
 * **Ordering is load-bearing.** This runs *before*
 * `POST /agent-media/bootstrap`, never after. A denied or missing
 * microphone must not consume an ACTIVE media identity: bootstrap mints
 * one under `one_active_identity_per_session`, and an identity minted
 * for a tab that can never produce audio would block the agent's own
 * retry with `already_active` until it expired.
 */

/**
 * Bounded failure vocabulary. Chosen by the agent's remedy, not by the
 * browser's error taxonomy: `DENIED` means "change a permission",
 * `UNAVAILABLE` means "there is no working microphone", and
 * `UNSUPPORTED` means "this browser/context cannot do it at all"
 * (an insecure origin, most often).
 */
export const MicrophoneFailure = Object.freeze({
  DENIED: 'microphone_denied',
  UNAVAILABLE: 'microphone_unavailable',
  UNSUPPORTED: 'microphone_unsupported',
  FAILED: 'microphone_failed',
});

/**
 * Audio only, and explicitly never video: a video constraint would make
 * the browser prompt for a camera the Workspace has no use for.
 */
export const MICROPHONE_CONSTRAINTS = Object.freeze({ audio: true, video: false });

function classify(error) {
  const name = error && error.name;
  if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
    return MicrophoneFailure.DENIED;
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') {
    return MicrophoneFailure.UNAVAILABLE;
  }
  if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') {
    return MicrophoneFailure.UNAVAILABLE;
  }
  return MicrophoneFailure.FAILED;
}

/**
 * Resolve with `{ stream }` or `{ failure }`. Never rejects.
 *
 * Returning a result rather than throwing is deliberate: every caller
 * has to decide what to do about a denied microphone, and a throw makes
 * it possible to forget and let the failure escape into a generic
 * catch that would then proceed to bootstrap.
 *
 * @param {object} [deps]
 * @param {object} [deps.mediaDevices] Injected for tests, which must
 *   never touch a real device.
 */
export async function acquireMicrophone({ mediaDevices = null } = {}) {
  const devices =
    mediaDevices ||
    (typeof navigator !== 'undefined' && navigator.mediaDevices ? navigator.mediaDevices : null);

  if (!devices || typeof devices.getUserMedia !== 'function') {
    return { stream: null, failure: MicrophoneFailure.UNSUPPORTED };
  }

  try {
    const stream = await devices.getUserMedia(MICROPHONE_CONSTRAINTS);
    if (!stream) {
      return { stream: null, failure: MicrophoneFailure.UNAVAILABLE };
    }
    return { stream, failure: null };
  } catch (error) {
    // The error object itself is discarded. It can carry a constraint
    // dump and a stack referencing page internals, and neither belongs
    // in product state or telemetry.
    return { stream: null, failure: classify(error) };
  }
}

/**
 * Stop every track. Idempotent, and tolerant of a stream whose tracks
 * were already stopped -- teardown runs on paths that may overlap.
 *
 * Not stopping tracks leaves the browser's recording indicator lit
 * after the agent has left the campaign, which reads as the product
 * still listening.
 */
export function releaseMicrophone(stream) {
  if (!stream || typeof stream.getTracks !== 'function') {
    return false;
  }
  let stopped = 0;
  for (const track of stream.getTracks()) {
    if (track && typeof track.stop === 'function') {
      track.stop();
      stopped += 1;
    }
  }
  return stopped > 0;
}
